import { useCallback } from 'react';
import type { DragEvent } from 'react';
import { useHandler } from '@framework/hooks/useHandler';

type Props = {
  hookDragEnter?: (e: DragEvent) => void;
  hookDragOver?: (e: DragEvent) => void;
  hookDragLeave?: (e: DragEvent) => void;
};

export type GetGuardProps = () => {
  onDragEnter: (e: DragEvent) => void;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: (e: DragEvent) => void;
};

export const useDragFileGuard = (props: Props) => {
  const { hookDragEnter, hookDragOver, hookDragLeave } = props;

  const noEffect = (e: DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'none';
      e.dataTransfer.dropEffect = 'none';
    }
  };

  // ファイルをDragしようとしているのか？
  const isEventTransferFiles = (e: DragEvent) => {
    const items = e.dataTransfer.items;
    if (!items) {
      return false;
    }

    const dtItems = [];
    for (let i = 0; i < items.length; i++) {
      dtItems.push(items[i]);
    }

    return dtItems.filter((item) => item.kind === 'file').length > 0;
  };

  // useHandler の戻り値は初回コミット前に呼ぶと例外になるため、描画中に呼ばれる getGuardProps ではなく、
  // 実際のイベント時にだけ呼ばれる各ハンドラに使う
  const handleDragEnter = useHandler((e: DragEvent) => {
    noEffect(e);
    if (hookDragEnter && isEventTransferFiles(e)) {
      hookDragEnter(e);
    }
  });

  const handleDragOver = useHandler((e: DragEvent) => {
    noEffect(e);
    if (hookDragOver) {
      hookDragOver(e);
    }
  });

  const handleDragLeave = useHandler((e: DragEvent) => {
    noEffect(e);
    if (hookDragLeave) {
      hookDragLeave(e);
    }
  });

  // getGuardProps関数をpropsに設定したコンポーネント上にDnDを試みると無効化されます
  //（これがないと、別タブに画像等のファイルの内容が表示されてしまう）
  // 毎回新しい関数を返すと、これを props に受ける memo コンポーネント（全ビュー）が再レンダーされる
  const getGuardProps: GetGuardProps = useCallback(
    () => ({ onDragEnter: handleDragEnter, onDragOver: handleDragOver, onDragLeave: handleDragLeave }),
    [handleDragEnter, handleDragOver, handleDragLeave]
  );

  return {
    getGuardProps,
  };
};
